// File: monorepo/web/frontend/src/components/business/selects/TagsMultiSelect.jsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import { TagInput, Loading } from "../../UIX";
import { useTagManager } from "../../../services/Services";

/**
 * Reusable Tags Multi-Select Component with server-side search
 *
 * @param {Array} value - Array of selected tag IDs
 * @param {function} onChange - Callback when value changes (receives array of IDs)
 * @param {string} error - Error message to display
 * @param {boolean} required - Whether field is required
 * @param {boolean} disabled - Whether field is disabled
 * @param {string} className - Additional CSS classes
 * @param {string} label - Custom label (defaults to "Tags")
 * @param {string} placeholder - Custom placeholder
 * @param {string} helperText - Helper text to display
 * @param {function} onUnauthorized - Callback for unauthorized errors
 */
function TagsMultiSelect({
  id,
  value = [],
  onChange,
  error,
  required = false,
  disabled = false,
  className = "",
  label = "Tags",
  placeholder = "Select tags...",
  helperText = "Select one or more tags",
  onUnauthorized = null,
}) {
  const tagManager = useTagManager();
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  // Use ref to track if component is mounted
  const isMounted = useRef(true);

  // Clean the value to ensure no empty strings
  const cleanValue = (val) => {
    if (!val || !Array.isArray(val)) return [];
    return val.filter(
      (v) => v !== null && v !== undefined && v !== "" && v !== "0" && v !== 0,
    );
  };

  // Fetch all options on initial load
  const fetchOptions = useCallback(async () => {
    try {
      if (!isMounted.current) return;

      setFetchError(null);

      if (import.meta.env.DEV) {
        console.log("TagsMultiSelect: Fetching all tag options");
      }

      // Fetch all tag select options
      const tagOptions = await tagManager.getTagSelectOptions(
        onUnauthorized,
        true, // Force refresh to get latest data
      );

      if (!isMounted.current) return;

      // Format options - should already be in {value, label} format
      const validOptions = (tagOptions || []).filter(
        (opt) => opt && opt.value && opt.label,
      );

      if (import.meta.env.DEV) {
        console.log(
          `TagsMultiSelect: Loaded ${validOptions.length} options`,
        );
      }
      setOptions(validOptions);

    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching tag options:", error);
      }
      if (isMounted.current) {
        setFetchError("Failed to load tags. Please try again.");
        setOptions([]);
      }
    }
  }, [tagManager, onUnauthorized]);


  // Initial load
  useEffect(() => {
    isMounted.current = true;
    setIsLoading(true);

    fetchOptions().finally(() => {
      if (isMounted.current) {
        setIsLoading(false);
      }
    });

    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - fetchOptions is stable via useCallback

  const handleChange = (newValue) => {
    // Clean the new value before passing it to onChange
    const cleanedValue = cleanValue(newValue);
    onChange(cleanedValue);
  };

  if (isLoading) {
    return (
      <div className={className}>
        <Loading size="sm" text="Loading tags..." />
      </div>
    );
  }

  // Use cleaned value for the TagInput
  const currentValue = cleanValue(value);

  return (
    <TagInput
      id={id}
      label={label}
      options={options}
      value={currentValue}
      onChange={handleChange}
      placeholder={placeholder}
      error={error || fetchError}
      disabled={disabled}
      required={required}
      helperText={helperText}
      className={className}
    />
  );
}


export default TagsMultiSelect;
